import { ArrowForward } from '@mui/icons-material';
import { Box, Button, Divider, Typography } from '@mui/material';
import { CollaborativeChartsReviewItem } from '@uplift/api';
import { useUpdateCollaborativeChartsReviewItemMutation } from '@uplift/queries';
import { FormikAutoSave, RadioControl, ScrollableView } from '@uplift/ui-v2';
import { Formik } from 'formik';
import React from 'react';
import { ChartsReviewFlagSection } from './Flagging';


const QuestionnaireQuestion: React.VFC<{
  title: React.ReactNode;
  subTitle?: React.ReactNode;
  name: string;
  disabled?: boolean;
}> = (props) => {
  return (
    <Box>
      <Typography variant="body1" fontWeight={500}>{props.title}</Typography>
      {props.subTitle && <Typography sx={{ mt: '2px', mb: '4px' }} variant="body2">{props.subTitle}</Typography>}
      <RadioControl
        name={props.name}
        disabled={props.disabled}
        values={[
          { value: 'yes', label: 'Yes' },
          { value: 'partially', label: 'Partially' },
          { value: 'no', label: 'No' },
          { value: 'not_applicable', label: 'Not applicable' },
        ]}
      />
    </Box>
  );
};

export const Questionnaire: React.VFC<{
  chartsReviewItem: CollaborativeChartsReviewItem;
  flagged: boolean;
  setFlagged: (flagged: boolean) => void;
  disabled?: boolean;
  onGoNext?: () => void;
}> = ({ chartsReviewItem, disabled, flagged, setFlagged, onGoNext }) => {
  const mutation = useUpdateCollaborativeChartsReviewItemMutation(chartsReviewItem.reviewId, chartsReviewItem.resourceId);

  return (
    <Formik
      enableReinitialize
      initialValues={chartsReviewItem}
      onSubmit={async (values: CollaborativeChartsReviewItem) => {
        mutation.mutateAsync(values);
      }}
    >
      {formik => (
        <>
          <ScrollableView sx={{ bgcolor: 'grey.50' }} showScrollShadow>
            {flagged && (
              <>
                <Box sx={{ bgcolor: 'grey.50', p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Typography variant="h4">Flag for Clinical Review</Typography>
                  <ChartsReviewFlagSection chartsReviewItem={chartsReviewItem} setFlagged={setFlagged} />
                </Box>
                <Divider />
              </>
            )}
            <Box sx={{ flex: 1, display: 'flex', bgcolor: 'common.white' }}>
              <FormikAutoSave delayMs={100} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
                <Typography variant="h4" sx={{ mt: 2 }}>Review Questions</Typography>
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="hpiClinicallyRelevant"
                  title="Is the HPI clinically relevant to the encounter, capturing patient narrative and concerns?"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="intervalUpdatesDocumented"
                  title="Are appropriate interval updates assessed and documented?"
                  subTitle="e.g. relevant symptoms, severity, treatment response, side effects, etc."
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="diagnosesSupportedByEvidence"
                  title="Are diagnoses supported by the patient history and objective findings?"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="medicationIndicationsDocumented"
                  title="Are medication indications documented, with clinical rationale provided when indicated?"
                  subTitle="e.g., unusual off-label use or unusual polypharmacy approach"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="mdmConsistentWithGuidelines"
                  title="Is medical decision making consistent with evidence-based guidelines and standards of care?"
                  subTitle="e.g., medications, dosing, medical considerations, diagnostic and monitoring steps, referrals, etc., when applicable"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="nonPharmInterventionsDocumented"
                  title="Are non-pharmacologic interventions considered and documented when appropriate?"
                  subTitle="e.g., education, therapy, diet, lifestyle, safety planning, etc."
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="followupPlanDocumented"
                  title="Is there a documented plan for follow up?"
                  subTitle="e.g., documented date or weeks"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="cptCodeAccurate"
                  title="Is the CPT code accurate and supported by the documented time and/or risk and complexity?"
                />
                <Divider />
                <QuestionnaireQuestion
                  disabled={disabled}
                  name="documentationSupportsContinuity"
                  title="Is documentation structure clear, up to date, consistent and supportive of continuity of care by another clinician?"
                  subTitle="e.g., no contradictions"
                />
                <Divider />

                <Box>
                  <Typography variant="body1" fontWeight={500}>Was the note submitted within 72 hours of visit completion?</Typography>
                  <RadioControl
                    name="noteSubmittedWithin72Hrs"
                    disabled
                    values={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ]}
                  />
                </Box>
              </Box>

            </Box>
          </ScrollableView>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              endIcon={<ArrowForward />}
              onClick={onGoNext}
              loading={formik.dirty || formik.isSubmitting}
              loadingPosition="end"
            >
              Next Chart
            </Button>
          </Box>
        </>
      )}
    </Formik>
  );
};
